import { useCallback, useEffect, useRef } from 'react';

type UseTimeout = {
  /** Schedule `fn`, replacing any timeout this hook already has pending. */
  set: (fn: () => void, ms: number) => void;
  /** Cancel the pending timeout, if any. */
  clear: () => void;
};

/**
 * A single self-cleaning timeout.
 *
 * Replaces the hand-rolled `useRef<ReturnType<typeof setTimeout>>` plus
 * `if (ref.current) clearTimeout(ref.current)` plus unmount-effect trio, which
 * was written out longhand at fourteen call sites. Clearing on unmount is the
 * part that was easy to forget.
 *
 * Deliberately holds one timer per hook instance: a component that needs two
 * independent timers calls it twice, which keeps each one's lifetime obvious.
 */
export function useTimeout(): UseTimeout {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const set = useCallback(
    (fn: () => void, ms: number) => {
      clear();
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        fn();
      }, ms);
    },
    [clear]
  );

  useEffect(() => clear, [clear]);

  return { set, clear };
}

export default useTimeout;
